import { useCallback, useMemo, useRef } from 'react';

const emptyArray = [];

export const isRegion = (slug, { regions } = {}) =>
  (regions || TINKERBELL_REGIONS).some((item) => item.value === slug);

const takeCountries = (regions) =>
  (regions || TINKERBELL_REGIONS).reduce((result, region) => [...result, ...region.list], []);

export const takeCountry = (region, { regions, number = 1 } = {}) => {
  const regionsList = regions || TINKERBELL_REGIONS;
  const regionSlug = region || Object.keys(regionsList)[0];
  const targetRegion = regionsList.find((item) => item.value === regionSlug);

  return targetRegion ? targetRegion.list.slice(0, number) : [];
};

function useRegions(
  regions,
  {
    enabled = emptyArray, // ['LATAM', ...]
    excluded = emptyArray, // ['worldwide', ...]
    decorateRegion,
  } = {}
) {
  const memoDecorateRegion = useRef();
  memoDecorateRegion.current = decorateRegion;

  const regionsList = useMemo(() => {
    const showAllRegions = !enabled.length || enabled.includes('ALL');

    if (showAllRegions && !excluded.length) return regions;

    return regions.reduce((result, region) => {
      // Exclude excluded regions
      if (excluded.includes(region.value)) return result;

      // Exclude not enabled regions
      if (!showAllRegions && !enabled.includes(region.value)) return result;

      return [...result, memoDecorateRegion.current ? memoDecorateRegion.current(region) : region];
    }, []);
  }, [regions, enabled.join(''), excluded.join('')]);

  const takeCountriesFromRegions = useCallback(() => takeCountries(regionsList), [regionsList]);

  const takeCountryByRegion = useCallback(
    (region, params = {}) => takeCountry(region, { regions: regionsList, ...params }),
    [regionsList]
  );

  return [
    regionsList,
    {
      takeCountries: takeCountriesFromRegions,
      takeCountry: takeCountryByRegion,
    },
  ];
}

export default useRegions;
